import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HomeCareFilters {
  search: string;
  county: string | null;
  service: string | null;
  hseApprovedOnly: boolean;
  minRating: number | null;
}

export const defaultHomeCareFilters: HomeCareFilters = {
  search: "",
  county: null,
  service: null,
  hseApprovedOnly: false,
  minRating: null,
};

export function useHomeCareProviders(filters: HomeCareFilters) {
  return useQuery({
    queryKey: ["home-care-providers", filters],
    queryFn: async () => {
      let query = supabase
        .from("home_care_providers")
        .select("*")
        .eq("is_active", true)
        .order("county_name")
        .order("name");

      if (filters.county) {
        query = query.eq("county_slug", filters.county);
      }
      if (filters.hseApprovedOnly) {
        query = query.eq("is_hse_approved", true);
      }
      if (filters.minRating) {
        query = query.gte("rating", filters.minRating);
      }
      if (filters.search) {
        query = query.or(
          `name.ilike.%${filters.search}%,short_description.ilike.%${filters.search}%,county_name.ilike.%${filters.search}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;

      // Client-side service filter (array column)
      let results = data || [];
      if (filters.service) {
        results = results.filter((p) =>
          p.services?.some(
            (s: string) => s.toLowerCase() === filters.service!.toLowerCase()
          )
        );
      }

      return results;
    },
  });
}

export function useHomeCareServiceOptions() {
  return useQuery({
    queryKey: ["home-care-service-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("home_care_providers")
        .select("services")
        .eq("is_active", true);

      if (error) throw error;

      const serviceSet = new Set<string>();
      data?.forEach((p) => p.services?.forEach((s: string) => serviceSet.add(s)));
      return Array.from(serviceSet).sort();
    },
  });
}
